"use client";

import clsx from "clsx";

/**
 * Monogram renders a circular badge in the brand display face with a 2-letter
 * abbreviation derived from the legal name. A small jurisdiction flag chip
 * sits in the bottom-end corner. Per Decision 9 in the plan, persona avatars
 * are monogrammed — never illustrated or photographic.
 *
 * Agent avatars use this same component but pass `glyph="◊"` (or similar
 * geometric character) instead of letters, plus pillar accent in `accent`.
 */
export function Monogram({
  name,
  flag,
  glyph,
  accent = "var(--state-verified)",
  size = 40,
  className,
}: {
  name: string;
  flag?: string;
  glyph?: string;
  accent?: string;
  size?: number;
  className?: string;
}) {
  const initials = glyph ?? name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");

  return (
    <span
      className={clsx("relative inline-flex items-center justify-center font-display font-semibold text-orbitra-ink select-none", className)}
      style={{ width: size, height: size }}
    >
      <span
        className="flex items-center justify-center rounded-full"
        style={{
          width: size,
          height: size,
          background: accent,
          boxShadow: `0 0 18px var(--state-verified-glow)`,
        }}
      >
        <span style={{ fontSize: Math.max(11, Math.round(size * 0.42)) }}>{initials}</span>
      </span>
      {flag && (
        <span
          className="absolute -end-0 -bottom-0 flex items-center justify-center rounded-full bg-orbitra-ink text-[8px] font-semibold uppercase text-orbitra-teal-soft"
          style={{
            width: Math.round(size * 0.42),
            height: Math.round(size * 0.42),
            border: "1px solid var(--state-verified-rim)",
          }}
        >
          {flag.toUpperCase()}
        </span>
      )}
    </span>
  );
}
